import { colors } from '@lmc/common/constants'
import type { Home } from '@lmc/ts-common'
import { getNominative } from 'common/utils'
import dayjs from 'dayjs'
import { findIndex, last } from 'lodash'
import type React from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { darken, lighten } from '../../../../../LEGACY/colorUtils'

interface Name {
  first: string
  last: string
}

interface Carer {
  name: Name
}

interface LogRevision {
  id: string
  timeLogged: string
  deleted?: boolean
  title?: string
  description?: string
  previousDescription?: string
  revokedBy?: Carer
  revokedAt?: string
  editedBy?: Carer
  editedAt?: string
}

interface Log {
  logRevisions: LogRevision[]
  loggedBy: Carer
  doneBySu?: boolean
}

interface LogRevisionItemProps {
  changelog: string[]
  isCurrent?: boolean
  log: Log
  revision: LogRevision
  showEdited?: boolean
  testId?: string
}

const LogRevisionItem: React.FC<LogRevisionItemProps> = ({
  changelog,
  isCurrent,
  log,
  revision,
  showEdited,
  testId
}) => {
  /**
   * The `revision` prop can be either a LogRevision item or a Log item.
   * Revisions use `revokedBy` for the carer who created the revision, while
   * Logs use `editedBy` for the carer who most recently created a revision.
   * The same goes for `revokedAt` vs. `editedAt`
   */
  const editedCarer = revision.revokedBy || revision.editedBy
  const editedTime = revision.revokedAt || revision.editedAt
  const revisionIndex = findIndex(log.logRevisions, ['id', revision.id])
  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )
  const nominative = getNominative(preferredPeopleNominative, 'Service user')

  const isLogRestoredRevision = () => {
    if (revisionIndex > 0) {
      const previousRevisionIndex = revisionIndex - 1
      return log.logRevisions[previousRevisionIndex]['deleted']
    }
    if (isCurrent) {
      return last(log.logRevisions)?.['deleted']
    }
    return false
  }

  const isRestored = isLogRestoredRevision()

  return (
    <Wrapper data-testid={testId || 'log-revision-item'}>
      <TitleContainer>
        <SmallText>
          {formatDate(revision.timeLogged)} - {formatName(log?.loggedBy)}
        </SmallText>
        {isCurrent ? (
          revision.deleted ? (
            <BadgeRemoved>Removed</BadgeRemoved>
          ) : (
            <Badge>Current</Badge>
          )
        ) : null}
      </TitleContainer>
      <Title>{revision.deleted ? 'Log Removed' : revision.title}</Title>
      <Description>
        {(!revision.deleted && !isRestored && revision.previousDescription) ||
          revision.description}
      </Description>
      {showEdited && (
        <SmallText>
          {revision.deleted
            ? 'Removed by '
            : isRestored
              ? 'Restored by '
              : 'Edited by '}
          {formatName(editedCarer)} on {formatDate(editedTime)}
        </SmallText>
      )}
      {!showEdited && log.doneBySu && (
        <SuInvolved>{nominative} was involved in writing this log</SuInvolved>
      )}
      {changelog.map((change, i) => (
        <SmallText key={i}>{change}</SmallText>
      ))}
    </Wrapper>
  )
}

const formatDate = (date: string | undefined): string | null =>
  date ? dayjs(date).format('DD-MM-YYYY HH:mm') : null

const formatName = (item: Carer | undefined): string | null =>
  item && item.name ? `${item.name.first} ${item.name.last}` : null

const Wrapper = styled.div`
  padding-bottom: 20px;
`

const TitleContainer = styled.div`
  align-items: center;
  display: flex;
`

const SmallText = styled.span`
  color: ${colors.darkgrey};
  font-size: 12px;
`
SmallText.displayName = 'SmallText'

const Badge = styled.div`
  background: ${lighten(colors.green, 80)};
  color: ${darken(colors.green, 40)};
  flex-shrink: 0;
  font-size: 12px;
  font-weight: bold;
  margin-left: 8px;
  padding: 3px 12px 2px;
  border-radius: 15px;
`

const BadgeRemoved = styled.div`
  background: ${colors.grey};
  color: ${colors.black};
  flex-shrink: 0;
  font-size: 12px;
  font-weight: bold;
  margin-left: 8px;
  padding: 3px 12px 2px;
`

const Title = styled.h3`
  font-size: 16px;
  font-weight: 400;
  line-height: 16px;
  margin: 2px 0px;
`
Title.displayName = 'Title'

const Description = styled.span`
  display: block;
  font-size: 14px;
  margin-bottom: 2px;
`

const SuInvolved = styled.span`
  color: #595959;
  display: block;
  margin-top: 2px;
  font-size: 12px;
`

export default LogRevisionItem
